import { Button } from "@astryxdesign/core/Button";
import { useToast } from "@astryxdesign/core/Toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
  Category,
  ChecklistEntry,
  Idea,
  IdeaCreate,
  IdeaPatch,
  LinkPreview,
} from "../../shared/types";
import { api, ApiError } from "./api";
import { CATEGORY_META, domainOf, type ParsedInput } from "./format";

export const ideasKey = ["ideas"] as const;

export function useIdeas() {
  return useQuery({
    queryKey: ideasKey,
    queryFn: api.listIdeas,
    staleTime: 30_000,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
  });
}

export function useCreateIdea() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["createIdea"],
    mutationFn: api.createIdea,
    onMutate: async (body: IdeaCreate) => {
      await qc.cancelQueries({ queryKey: ideasKey });
      const prev = qc.getQueryData<Idea[]>(ideasKey);
      const now = Date.now();
      const temp: Idea = {
        id: `tmp-${now}`,
        title: body.title,
        notes: body.notes ?? "",
        category: body.category ?? "do",
        status: "open",
        pinned: false,
        sortOrder: 0,
        due: body.due ?? null,
        links: body.links ?? [],
        checklist: body.checklist ?? [],
        suggestions: null,
        research: null,
        createdAt: now,
        updatedAt: now,
        doneAt: null,
      };
      qc.setQueryData<Idea[]>(ideasKey, (list) => [temp, ...(list ?? [])]);
      return { prev, tempId: temp.id };
    },
    onSuccess: (created, _body, ctx) => {
      qc.setQueryData<Idea[]>(ideasKey, (list) =>
        (list ?? []).map((i) => (i.id === ctx.tempId ? created : i)),
      );
    },
    onError: (_e, _body, ctx) => {
      if (ctx?.prev) qc.setQueryData(ideasKey, ctx.prev);
    },
  });
}

export function usePatchIdea() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["patchIdea"],
    mutationFn: ({ id, patch }: { id: string; patch: IdeaPatch }) => api.patchIdea(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ideasKey });
      const prev = qc.getQueryData<Idea[]>(ideasKey);
      qc.setQueryData<Idea[]>(ideasKey, (list) =>
        (list ?? []).map((i) => (i.id === id ? { ...i, ...patch, updatedAt: Date.now() } : i)),
      );
      return { prev };
    },
    onSuccess: (updated) => {
      qc.setQueryData<Idea[]>(ideasKey, (list) =>
        (list ?? []).map((i) => (i.id === updated.id ? updated : i)),
      );
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ideasKey, ctx.prev);
    },
  });
}

export function useDeleteIdea() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["deleteIdea"],
    mutationFn: (id: string) => api.deleteIdea(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ideasKey });
      const prev = qc.getQueryData<Idea[]>(ideasKey);
      qc.setQueryData<Idea[]>(ideasKey, (list) => (list ?? []).filter((i) => i.id !== id));
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(ideasKey, ctx.prev);
    },
  });
}

/** Delete with an Undo toast; shared by the dialog and row swipes. */
export function useDeleteWithUndo() {
  const toast = useToast();
  const deleteIdea = useDeleteIdea();
  const createIdea = useCreateIdea();
  return useCallback(
    (idea: Idea) => {
      deleteIdea.mutate(idea.id);
      toast({
        body: `Deleted “${idea.title}”`,
        uniqueID: "idea-deleted",
        endContent: (
          <Button
            label="Undo"
            size="sm"
            variant="ghost"
            onClick={() =>
              createIdea.mutate({
                title: idea.title,
                category: idea.category,
                notes: idea.notes,
                due: idea.due,
                links: idea.links,
                checklist: idea.checklist,
              })
            }
          />
        ),
      });
    },
    [createIdea, deleteIdea, toast],
  );
}

/**
 * The quick-add brain, a three-stage pipeline that never blocks the UI:
 *  1. create instantly (optimistic);
 *  2. fetch link previews for every URL, upgrade a placeholder domain-title;
 *  3. Mistral enrichment — auto-category, cleaned title, checklist
 *     suggestions. The if* guards keep AI from clobbering user edits.
 */
export function useAddIdea() {
  const qc = useQueryClient();
  const toast = useToast();
  const create = useCreateIdea();
  const patchIdea = usePatchIdea();

  const deleteIdea = useDeleteIdea();

  return useCallback(
    (parsed: ParsedInput, category: Category) => {
      const links: LinkPreview[] = parsed.urls.map((url) => ({ url, domain: domainOf(url) }));
      const isDomainTitle = links.length > 0 && parsed.title === links[0]?.domain;

      const replaceInCache = (updated: Idea) =>
        qc.setQueryData<Idea[]>(ideasKey, (list) =>
          (list ?? []).map((i) => (i.id === updated.id ? updated : i)),
        );

      /** Fold the new idea into an existing one: union links/checklist, carry notes. */
      const mergeIdeas = async (sourceId: string, targetId: string) => {
        const list = qc.getQueryData<Idea[]>(ideasKey) ?? [];
        const source = list.find((i) => i.id === sourceId);
        const target = list.find((i) => i.id === targetId);
        if (!source || !target) return;

        const mergedLinks = [...target.links];
        for (const l of source.links) {
          if (!mergedLinks.some((x) => x.url === l.url)) mergedLinks.push(l);
        }
        const mergedChecklist: ChecklistEntry[] = [...target.checklist];
        for (const s of source.checklist) {
          if (!mergedChecklist.some((x) => x.text === s.text)) mergedChecklist.push(s);
        }
        const patch: IdeaPatch = { links: mergedLinks, checklist: mergedChecklist };
        // A plain-text title is information; a domain placeholder isn't.
        const titleIsDomain = source.links.some((l) => l.domain === source.title);
        const carried = [titleIsDomain ? "" : source.title, source.notes]
          .filter(Boolean)
          .join("\n");
        if (carried) patch.notes = target.notes ? `${target.notes}\n${carried}` : carried;

        deleteIdea.mutate(sourceId);
        await patchIdea.mutateAsync({ id: targetId, patch }).catch(() => {});
        toast({ body: `Merged into “${target.title}”`, uniqueID: "merged" });

        // The merged set may make the old comparison title stale
        // ("A vs B" + new C) — let enrichment re-synthesize it.
        try {
          const { idea: refreshed } = await api.enrich({
            id: targetId,
            ifCategory: target.category,
            ifTitle: target.title,
          });
          replaceInCache(refreshed);
        } catch {
          // best-effort
        }
      };

      create.mutate(
        { title: parsed.title, notes: parsed.notes, category, links },
        {
          onSuccess: async (idea) => {
            let expectedTitle = idea.title;

            if (links.length > 0) {
              const metas = await Promise.allSettled(links.map((l) => api.linkMeta(l.url)));
              const merged = links.map((l, i) => {
                const m = metas[i];
                return m?.status === "fulfilled" ? m.value : l;
              });
              const patch: IdeaPatch = { links: merged };
              const firstTitle = merged[0]?.title;
              const current = qc.getQueryData<Idea[]>(ideasKey)?.find((i) => i.id === idea.id);
              // Single link → its page title becomes the idea title. Multiple
              // links (comparison shopping) → keep the placeholder and let
              // enrichment synthesize a title covering all of them.
              if (
                isDomainTitle &&
                links.length === 1 &&
                firstTitle &&
                (!current || current.title === idea.title)
              ) {
                patch.title = firstTitle;
                expectedTitle = firstTitle;
              }
              // Await so enrichment reads the row with previews already saved.
              await patchIdea.mutateAsync({ id: idea.id, patch }).catch(() => {});
            }

            try {
              const before =
                qc.getQueryData<Idea[]>(ideasKey)?.find((i) => i.id === idea.id) ?? idea;
              const { idea: enriched, similarTo } = await api.enrich({
                id: idea.id,
                ifCategory: category,
                ifTitle: expectedTitle,
              });
              replaceInCache(enriched);
              if (similarTo) {
                const dismiss = toast({
                  body: `✨ Similar to “${similarTo.title}”`,
                  uniqueID: "enrich-similar",
                  autoHideDuration: 10_000,
                  endContent: (
                    <Button
                      label="Merge"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        dismiss();
                        void mergeIdeas(enriched.id, similarTo.id);
                      }}
                    />
                  ),
                });
              } else if (enriched.category !== before.category) {
                toast({
                  body: `✨ Filed under ${CATEGORY_META[enriched.category].label}`,
                  uniqueID: "enrich",
                });
              }
            } catch {
              // enrichment is best-effort
            }
          },
        },
      );
    },
    [create, patchIdea, deleteIdea, qc, toast],
  );
}
